import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import produce from 'immer';
import { isEqual } from 'lodash-es';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { MapApiService, MapStateService } from 'src/app/modules/cop/services';
import { LayerDataChangeEvent, LayerDataChangeType } from 'src/app/modules/cop/store/cop/models/layer-data-change-event.model';
import * as listsActions from 'src/app/modules/cop/store/lists/lists.actions';
import * as listsSelectors from 'src/app/modules/cop/store/lists/lists.selectors';
import { MapDataWorkerService } from 'src/app/modules/cop/web-workers/client/map-data-worker.service';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import {
  DocumentWriter,
  Filter,
  FilterGroupType,
  LayerDocumentsInClickRadiusRequest,
  QueryParams,
  RuleOperator
} from 'src/app/modules/core/store/models';
import { Document, DocumentsSet, FieldDataType, SystemFieldNames } from 'src/app/modules/core/store/models/document.model';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { GUID_EMPTY } from '@app/core/constants';
import { ContentShareApiService } from 'src/app/modules/management/store/content-share/content-share-api.service';
import { v4 as uuidv4 } from 'uuid';
import * as notificationActions from '../../../notifications/store/notifications.actions';

import { NgxSpinnerService } from 'ngx-spinner';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import * as siteWatchActions from '../../../site-watch/store/site-watch.actions';
import { MapDrawingService } from '../../drawing/map-drawing.service';
import { DataApiService } from '../../services/data/data-api.service';
import { BboxGeometry } from '../../services/geometry.model';
import { FolderUtils } from '../../services/map/folder-utils';
import { WorkflowUtils } from '../../services/map/workflow-utils';
import { UtilService } from '../../services/util.service';
import { MapLayerSummaryByStatus } from '../lsd/models/map-layer-summary.model';
import * as rsdActions from '../rsd/rsd.actions';
import * as rsdSelectors from '../rsd/rsd.selectors';
import * as copActions from './cop.actions';
import * as copSelectors from './cop.selectors';
import {
  DisplayOptions,
  DocumentsToDelete,
  Folder,
  Layer,
  LayerDocumentUIdsSet,
  LayerTypes,
  Map,
  MapSettings,
  SectionType,
  TemporaryFolderUIds,
  TemporaryLayerUIds
} from './models';
import { CalculatedLayerFilterOptions } from './models/map-state.model';
import { FilterRuntimeService } from 'src/app/modules/core/services/filter-runtime/filter-runtime.service';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';
import { QuickFilterHelperService } from '../../services/quick-filter-helper.service';
import { MapLayerQueryBuildService } from '../../services/data/map-layer-query-build.service';
import { SiteWatchOfficerService } from '../../services/sitewatch-officers.service';
import { MapWallDisplayActions, MapWallDisplayFeature } from '@app/features/operating-picture/map-wall-display/store';
import { UserStateService } from '@app/modules/core/services/user-state.service';
import { StateCategory } from '@app/modules/core/store/models/user-state.model';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { DrawingToolsService } from '../../drawing/drawing-tools.service';

@Injectable()
export class CopEffects {
  private filterRuntimeService = new FilterRuntimeService(this.loggerService.getLogger('Cop.FilterRuntimeService'));

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private mapApiService: MapApiService,
    private mapStateService: MapStateService,
    private mapDataWorkerService: MapDataWorkerService,
    private datasetApiService: DatasetApiService,
    private dataApiService: DataApiService,
    private translate: TranslateService,
    private notify: SystemNotificationsService,
    private mapDrawingService: MapDrawingService,
    private mapDrawingTools: DrawingToolsService,
    private spinnerService: NgxSpinnerService,
    private contentShareApiService: ContentShareApiService,
    private distanceService: DistanceService,
    private utilService: UtilService,
    private loggerService: LoggerService,
    private siteWatchOfficerService: SiteWatchOfficerService,
    private userStateService: UserStateService,
    private userContext: UserContextService
  ) {}

  //
  // loads the map from the database, inits the MapWithState
  // and then tells the web worker to start the data population process
  //
  loadSelectedMapAndDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadSelectedMapAndDocument),
      withLatestFrom(this.store$.select(copSelectors.selectMapSettings), this.store$.select(MapWallDisplayFeature.selectIsActive)),
      switchMap(([action, settings, isWallDisplayActive]) => {
        const { mapUId } = action;
        if (mapUId === GUID_EMPTY) {
          return [];
        }

        // show spinner if we are planning to call clearMap
        this.spinnerService.show('map');

        const forkActions: Record<string, Observable<any>> = {};

        forkActions.theMap = this.mapApiService.getMap(mapUId).pipe(catchError(err => of(copActions.setError({ err }))));

        if (!!action.datasetUId && !!action.documentUId) {
          // If a document and dataset were passed into the route, we need to load them
          // and create a new temporary layer to hold the document before sending the selectedMapLoaded event

          forkActions.dataset = this.datasetApiService.getDataset(action.datasetUId, { includeWorkflow: true }).pipe(
            catchError(err => {
              const errorMessage = this.translate.instant(`MAP.ERROR-LINK-DATASET`);
              this.notify.error(errorMessage);
              console.error(errorMessage + ': ' + action.datasetUId);
              return of(null);
            })
          );

          forkActions.document = this.dataApiService.getDocument(action.datasetUId, action.documentUId).pipe(
            catchError(err => {
              const errorMessage = this.translate.instant(`MAP.ERROR-LINK-DOCUMENT`);
              this.notify.error(errorMessage);
              console.error(errorMessage + ': ' + action.documentUId);
              return of(null);
            })
          );
        }

        return forkJoin(forkActions).pipe(
          mergeMap(({ theMap, dataset = null, document = null }) => {
            let layer = null;
            if (!!dataset && !!document) {
              let layerName = this.translate.instant(`MAP.SELECTED-DOCUMENT-LAYER-NAME`);
              layer = createSelectedDocumentLayer(dataset, layerName, action.documentUId);

              let rootFolder: Folder = theMap.folders[0];

              let newFolder = {
                uId: TemporaryFolderUIds.SelectedDocumentFolderUId,
                parentUId: rootFolder.uId,
                name: this.translate.instant(`MAP.SELECTED-DOCUMENT-FOLDER-NAME`),
                layers: [layer],
                sectionType: SectionType.None,
                sortOrder: 999,
                folders: []
              } as Folder;

              theMap.folders[0].folders.push(newFolder);
            }

            const mapWithState = this.mapStateService.initMapStateFromMap(theMap, settings);

            if (!!dataset && !!!mapWithState.datasetsSet[dataset.uId]) {
              mapWithState.datasetsSet[dataset.uId] = dataset;
            }

            const actions = [];

            actions.push(copActions.setIsMaploadingToStore({ loading: true }));

            if (!isWallDisplayActive) {
              actions.push(copActions.clearMap({ options: null }));
            }

            actions.push(copActions.loadMapWithStateCompleteToStore({ mapWithState }));
            actions.push(copActions.restoreUserState());
            actions.push(copActions.loadDatasetsForSelectedMap());
            actions.push(copActions.setIsMaploadingToStore({ loading: false }));

            if (!!dataset && !!document) {
              actions.push(listsActions.setCardVisibilityToStore({ isVisible: false }));
              actions.push(
                rsdActions.setSelectedDocument({
                  layerUId: layer.uId,
                  documentUId: action.documentUId,
                  isDrawerOpen: true,
                  replaceStack: true
                })
              );
              actions.push(
                copActions.zoomToDocument({
                  layer: layer,
                  dataset: dataset,
                  document: document
                })
              );
            }

            return actions;
          })
        );
      })
    )
  );

  refreshSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.refreshSelectedMap),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        this.mapDrawingService.clearFeatures();
        const mapWithState = produce(mapState, draft => {
          this.mapStateService.setMapStateToLoading(draft);
        });
        this.mapDataWorkerService.reset();
        return [copActions.loadMapWithStateCompleteToStore({ mapWithState }), copActions.loadDatasetsForSelectedMap()];
      })
    )
  );

  editMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.editMap),
      withLatestFrom(this.store$.select(copSelectors.selectMaps), this.store$.select(copSelectors.selectMapSettings)),
      switchMap(([action, maps, settings]) => {
        const map = maps.find(m => m.uId === action.uId);
        const mapWithState = this.mapStateService.initMapStateFromMap(map, settings);
        return [copActions.editMapWithStateCompleteToStore({ mapWithState })];
      })
    )
  );

  clearMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.clearMap),
      switchMap(action => {
        this.mapDataWorkerService.reset();
        this.mapDrawingService.clearAll(action.options);

        return [
          listsActions.clearListsToStore(),
          copActions.clearSelectedDocumentsToStore(),
          copActions.clearFloorValues(),
          rsdActions.hideDrawer({ clearDocumentStack: true }),
          MapWallDisplayActions.hideMapWallDisplay()
        ];
      })
    )
  );

  //
  // loads the datasets that are referenced by the current map
  //
  loadDatasetsForSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadDatasetsForSelectedMap),
      withLatestFrom(this.store$.select(copSelectors.selectLayers), this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([, layers, mapState]) => {
        const { datasetsSet } = mapState;

        const datasetUIds = [...new Set(layers.filter(l => !!l.datasetUId && !!datasetsSet[l.datasetUId]).map(l => l.datasetUId))];

        const queryParams: QueryParams = {
          skip: 0,
          take: 1000,
          where: null,
          sortField: 'name',
          sortDirection: 'Ascending'
        };

        return this.datasetApiService.getDatasets(queryParams, true, true, datasetUIds).pipe(
          switchMap(res => [
            copActions.loadDatasetsForSelectedMapCompleteToStore({ datasets: res.data }),
            copActions.populateAndTrackSelectedMap()
          ]),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  generateFloorsForMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadDatasetsForSelectedMapCompleteToStore),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([{ datasets }, currentMap]) => {
        const layers = currentMap?.layers;

        if (!layers || !layers.length) {
          return [];
        }

        const userContext = this.userContext.getContext();
        const locale =
          !!userContext.cultureCode && userContext.cultureCode?.indexOf('-') > 0
            ? userContext.cultureCode.substring(0, userContext?.cultureCode?.indexOf('-'))
            : 'en';

        const floorValues = Array.from(
          layers.reduce((acc, layer) => {
            const floorField = layer?.displayOptions?.floorFilterSourceField;
            const dataset = datasets.find(ds => ds.uId === layer?.datasetUId);

            if (!floorField || !dataset) {
              return acc;
            }

            const picklistValues =
              dataset.schema?.fields?.find(({ systemName }) => systemName === floorField)?.picklistValues?.map(({ value }) => value) ?? [];

            picklistValues.forEach(v => acc.add(v));

            return acc;
          }, new Set<string>())
        ).sort((a, b) => a.localeCompare(b, locale, { numeric: true }));

        return [copActions.setFloorsForSelectedMap({ floorValues })];
      })
    )
  );

  //
  // Tells the web worker to start populating and tracking data
  // for the specified map
  //
  populateAndTrackSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.populateAndTrackSelectedMap),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedFloor),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections)
      ),
      switchMap(([, currentMapState, floor, layerQuickFilters]) => {
        const { datasetsSet, layersSet, map } = currentMapState;

        const layerUIds = Object.values(layersSet).reduce((acc, layer) => {
          if (!!layer.datasetUId && !!datasetsSet[layer.datasetUId]) {
            acc.push(layer.uId);
          }

          return acc;
        }, [] as string[]);

        this.mapDataWorkerService.populateAndTrackMap({
          map,
          datasetsSet,
          layerQuickFilters,
          floor
        });

        return [copActions.setLayersLoading({ layerUIds })];
      })
    )
  );

  //
  // Called from the web worker client to process layer data
  // change events. Events are dispatched by event type.
  //
  processLayerDataChangeEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.processLayerDataChangeEvents),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { events } = action;

        const actions = [];
        let currentMapState = mapState;
        let hasAlertLayerChange = false;

        events?.forEach(e => {
          if (this.siteWatchOfficerService?.officerLayer?.uId === e.layerUId) {
            // Process mappedin officer data separately
            this.siteWatchOfficerService.handleOfficerEvent(e);
            return;
          }
          if (e.type == LayerDataChangeType.Add) {
            currentMapState = this.mapStateService.addLayerDocumentsForEvent(currentMapState, e);
            actions.push(
              copActions.checkDataChanged({
                layerUId: e.layerUId,
                documentUIds: Object.keys(e.changes),
                action: 'add'
              })
            );
            if (currentMapState?.datasetsSet[currentMapState?.layersSet[e?.layerUId]?.datasetUId]?.type === DatasetType.Alerts) {
              hasAlertLayerChange = true;
            }
          } else if (e.type == LayerDataChangeType.Update) {
            currentMapState = this.mapStateService.updateLayerDocumentsForEvent(currentMapState, e);
            actions.push(
              copActions.checkDataChanged({
                layerUId: e.layerUId,
                documentUIds: Object.keys(e.changes),
                action: 'update'
              })
            );
            if (currentMapState?.datasetsSet[currentMapState?.layersSet[e?.layerUId]?.datasetUId]?.type === DatasetType.Alerts) {
              hasAlertLayerChange = true;
            }
          } else if (e.type == LayerDataChangeType.Remove) {
            currentMapState = this.mapStateService.removeLayerDocumentsForEvent(currentMapState, e);
            actions.push(
              copActions.checkDataChanged({
                layerUId: e.layerUId,
                documentUIds: Object.keys(e.changes),
                action: 'delete'
              })
            );
            if (currentMapState?.datasetsSet[currentMapState?.layersSet[e?.layerUId]?.datasetUId]?.type === DatasetType.Alerts) {
              hasAlertLayerChange = true;
            }
          } else if (e.type == LayerDataChangeType.Replace) {
            currentMapState = this.mapStateService.replaceLayerDocumentsForEvent(currentMapState, e);
            actions.push(
              copActions.checkDataChanged({
                layerUId: e.layerUId,
                documentUIds: Object.keys(e.changes),
                action: 'update'
              })
            );
            if (currentMapState?.datasetsSet[currentMapState?.layersSet[e?.layerUId]?.datasetUId]?.type === DatasetType.Alerts) {
              hasAlertLayerChange = true;
            }
          }

          currentMapState = produce(currentMapState, draft => {
            if (Object.keys(e.changes).length === 0 || e.type == LayerDataChangeType.Error) {
              if (!draft.layerStates) {
                draft.layerStates = {};
              }
              if (!!!draft.layerStates[e.layerUId]) {
                draft.layerStates[e.layerUId] = {};
              }
              draft.layerStates[e.layerUId].isLoading = false;
              draft.layerStates[e.layerUId].isError = e.type == LayerDataChangeType.Error;
            }
          });
        });

        if (events?.length > 0 || actions.length > 0) {
          actions.push(copActions.loadMapWithStateCompleteToStore({ mapWithState: currentMapState }));

          if (hasAlertLayerChange) {
            actions.push(
              copActions.calculateWorkflowStatusSummary({
                summaryItems: WorkflowUtils.getWorkflowStatusSummaryModelFromMap(currentMapState.map)
              })
            );
          }
        }

        return actions;
      })
    )
  );

  setSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.setSelectedMap),
      withLatestFrom(this.store$.select(copSelectors.selectMaps), this.store$.select(fromUserProfile.selectOrganization)),
      tap(() => this.mapDrawingTools.teardown()),
      switchMap(([action, maps, organization]) => {
        if (!maps?.length) {
          return [];
        }

        return this.userStateService.getUserState(StateCategory.Map, 'cop').pipe(
          switchMap(userState => {
            const actions = [];
            const selectedMapUId = action?.uId ?? userState?.selectedMapUId ?? organization?.mapSettings?.defaultMapUId ?? null;

            if (!!selectedMapUId) {
              const mapToLoad = maps.find(m => m.uId === selectedMapUId);

              // if map exists, check for existing user map settings
              if (!!mapToLoad) {
                const { mapSettings } = userState?.maps?.find(({ mapUId }) => mapUId === selectedMapUId) ?? { mapSettings: null };

                if (!!mapSettings) {
                  // this is probably causing COP Main to retrigger events listening to map settings...
                  actions.push(
                    copActions.setUserMapSettingsToLoadToStore({
                      mapSettings,
                      selectedMapUId
                    })
                  );
                }

                actions.push(copActions.goToMap({ map: mapToLoad }));
              } else {
                // default to first map in loaded list if for some reason selected uId wasn't from loaded list.
                console.warn(`Requested Map for UId ${selectedMapUId} not found.`);
                actions.push(copActions.goToMap({ map: maps[0] }));
              }
            }

            return actions;
          })
        );
      })
    )
  );

  goToMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.goToMap),
      withLatestFrom(
        this.store$.select(copSelectors.selectLinkDocumentUId),
        this.store$.select(copSelectors.selectLinkDocumentDatasetUId),
        this.store$.select(copSelectors.selectIsOverviewMode)
      ),
      switchMap(([action, linkDocumentUId, linkDatasetUId, isOverviewMode]) => {
        let path = ['cop', action.map.uId];

        const actions = [];

        if (linkDocumentUId && linkDatasetUId) {
          path.push('dataset', linkDatasetUId, 'document', linkDocumentUId);
          // Clear linked document loading state
          actions.push(copActions.setLinkDocumentToStore({ datasetUId: null, documentUId: null }));
        }

        // -- leaving here for artifact purposes ---
        // actions.push(notificationActions.clearAllNotifications({ clearPermanent: false }));

        if (isOverviewMode === true) {
          actions.push(MapWallDisplayActions.showMapWallDisplay({ stringifiedWallConfiguration: action.map.wallConfiguration }));
          actions.push(copActions.openCloseLeftDrawerToStore({ isOpen: false }));
        }
        actions.push(
          copActions.loadSelectedMapAndDocument({
            mapUId: action.map.uId,
            datasetUId: linkDatasetUId,
            documentUId: linkDocumentUId
          })
        );
        actions.push(
          fromRouting.go({
            routeData: { path: path },
            extendOnly: true
          }),
          copActions.calculateWorkflowStatusSummary({ summaryItems: [] })
        );

        return actions;
      })
    )
  );

  loadMapsForUserAndSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadMapsForUserAndSelect),
      switchMap(action => {
        const params: QueryParams = {
          skip: 0,
          take: 1000,
          where: null,
          sortField: 'name',
          sortDirection: 'Ascending'
        };

        return this.mapApiService.getMaps(params).pipe(
          switchMap(res => [
            copActions.setLinkDocumentToStore({ datasetUId: action.datasetUId, documentUId: action.documentUId }),
            copActions.loadMapsForUserCompleteToStore({ maps: res.data }),
            copActions.setSelectedMap({ uId: action.mapUId })
          ]),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  saveUserState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(copActions.saveUserState),
        withLatestFrom(
          this.store$.select(copSelectors.selectSelectedMapState),
          this.store$.select(copSelectors.selectMapSettings),
          this.store$.select(copSelectors.selectMapPosition),
          this.store$.select(copSelectors.selectMaps)
        ),
        switchMap(([, mapState, mapSettings, mapPosition, maps]) => {
          if (!!!mapState || !!!mapState.map || mapState?.map.uId === GUID_EMPTY) {
            return [];
          }

          const mapUId = mapState.map.uId;

          return this.userStateService.getUserState(StateCategory.Map, 'cop').pipe(
            switchMap(storeValue => {
              if (!storeValue || !storeValue.maps) {
                const newStore = {
                  selectedMapUId: mapUId,
                  maps: [
                    {
                      mapUId: mapUId,
                      mapSettings: {
                        ...mapSettings,
                        mapPosition: mapPosition
                      }
                    }
                  ]
                };

                return this.userStateService.setUserState(StateCategory.Map, 'cop', JSON.stringify(newStore));
              }

              const mapIndex = storeValue.maps.findIndex(m => m.mapUId === mapUId);

              if (mapIndex !== -1) {
                const newMap = {
                  mapSettings: {
                    ...mapSettings,
                    mapPosition: mapPosition
                  },
                  mapUId: mapUId
                };

                if (storeValue.maps[mapIndex] != newMap) {
                  storeValue.maps[mapIndex] = newMap;
                }

                storeValue.selectedMapUId = mapUId;
              } else {
                storeValue.maps.push({
                  mapUId: mapUId,

                  mapSettings: {
                    mapPosition: mapPosition
                  }
                });
                storeValue.selectedMapUId = mapUId;
              }

              storeValue.maps = storeValue.maps.filter(m => maps.findIndex(_m => _m.uId === m.mapUId) > -1);

              return this.userStateService.setUserState(StateCategory.Map, 'cop', JSON.stringify(storeValue));
            })
          );
        })
      ),
    { dispatch: false }
  );

  restoreUserState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.restoreUserState),
      withLatestFrom(
        this.store$.select(copSelectors.selectMapSettingsFromStorageToLoad),
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(MapWallDisplayFeature.selectIsActive)
      ),
      switchMap(([action, settingsFromStorage, selectedMapState, isWallDisplayActive]) => {
        const mapSettings = settingsFromStorage?.mapSettings;

        if (selectedMapState.map.uId !== settingsFromStorage?.selectedMapUId || !mapSettings || !mapSettings.mapPosition) {
          return [];
        }

        if (!isWallDisplayActive) {
          this.mapDrawingService.setCenterAndZoom(
            mapSettings.mapPosition.center.lat,
            mapSettings.mapPosition.center.lng,
            mapSettings.mapPosition.zoomLevel
          );
        }

        return [copActions.setUiStateFromStoredSettingsToStore()];
      })
    )
  );

  saveSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.saveSelectedMap),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectIsFolderEdit),
        this.store$.select(copSelectors.selectIsLayerEdit),
        this.store$.select(MapWallDisplayFeature.selectConfiguration)
      ),
      switchMap(([action, mapState, isFolderEdit, isLayerEdit, wallConfiguration]) => {
        let actions = [];

        if (mapState.map?.version === 1 && (isFolderEdit || isLayerEdit)) {
          actions.push(copActions.setTrySaveMapToStore({ isTrySaveMap: true }));
        }
        let newState = mapState;
        if (!!action.mapName) {
          newState = produce(mapState, draftState => {
            draftState.map.name = action.mapName;
            if (!!wallConfiguration && !(Object.keys(wallConfiguration).length === 0)) {
              draftState.map.wallConfiguration = !!wallConfiguration ? JSON.stringify(wallConfiguration) : null;
            }
          });
        }

        if (actions.length > 0) {
          actions.unshift(copActions.setDraftMapToStore({ mapState: newState }));
          return actions;
        }

        if (mapState.map.uId === GUID_EMPTY) {
          return [copActions.saveUserState(), copActions.createMap({ map: newState.map, cancelEdit: action.cancelEdit })];
        }

        return [
          copActions.setLayerVisibilitiesFromCurrentLayerState(),
          copActions.saveUserState(),
          copActions.updateMap({
            mapState: newState,
            cancelEdit: action.cancelEdit,
            refreshLayersOnly: action.refreshLayersOnly,
            refreshLabelsOnly: action.refreshLabelsOnly
          })
        ];
      })
    )
  );

  createMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.createMap),
      switchMap(action => {
        const mapToCreate = action.map || ({} as Map);

        return this.mapApiService.createMap(mapToCreate).pipe(
          switchMap(map => {
            const actions = [];
            if (action.cancelEdit) {
              actions.push(MapWallDisplayActions.hideMapWallDisplay());
            }
            const settings: MapSettings = {} as MapSettings;
            const mapWithState = this.mapStateService.initMapStateFromMap(map, settings);

            actions.push(copActions.storeCreatedMapToStore({ mapState: mapWithState, cancelEdit: action.cancelEdit }));
            actions.push(copActions.setSelectedMap({ uId: mapWithState.map.uId }));

            const message = this.translate.instant('MAP.MAP-SAVED');
            this.notify.success(message);
            return actions;
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  updateMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateMap),
      switchMap(({ mapState, cancelEdit, refreshLayersOnly, refreshLabelsOnly }) =>
        this.mapApiService.updateMap(mapState.map).pipe(
          switchMap(updatedMap => {
            const actions = [];

            if (cancelEdit) {
              actions.push(MapWallDisplayActions.hideMapWallDisplay());
            }

            actions.push(copActions.updateMapCompleteToStore({ mapState, cancelEdit }));

            if (!Boolean(refreshLayersOnly)) {
              actions.push(copActions.setSelectedMap({ uId: mapState.map.uId }));
              const message = this.translate.instant('MAP.MAP-SAVED');
              this.notify.success(message);
            } else {
              Object.keys(refreshLayersOnly).forEach(l => {
                if (refreshLabelsOnly) {
                  this.mapDrawingService.refreshLayerDocumentLabels(l, refreshLayersOnly[l]);
                } else {
                  this.mapDrawingService.refreshLayerDocuments(l, refreshLayersOnly[l]);
                }
              });
            }

            return actions;
          }),
          catchError(err => of(copActions.setError({ err })))
        )
      )
    )
  );

  cancelEditMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.cancelEditMap),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectMaps),
        this.store$.select(copSelectors.selectIsFolderEdit),
        this.store$.select(copSelectors.selectIsLayerEdit),
        this.store$.select(copSelectors.selectPreviouslySelectedMapUId)
      ),
      switchMap(([action, mapState, maps, isFolderEdit, isLayerEdit, previousMapUId]) => {
        const actions = [];
        if (mapState.map?.version === 1 && isFolderEdit) {
          actions.push(copActions.cancelEditFolderToStore());
        }
        if (mapState.map?.version === 1 && isLayerEdit) {
          actions.push(copActions.cancelEditLayer());
        }

        if (mapState.map.uId === GUID_EMPTY) {
          actions.push(copActions.removeUnsavedMapAfterCancel());
        }

        const uId = mapState.map.uId === GUID_EMPTY ? previousMapUId || maps?.find(m => m.uId !== GUID_EMPTY)?.uId : mapState.map.uId;

        return [
          ...actions,
          copActions.setSelectedMap({ uId }),
          copActions.setCancelEditMapToStore(),
          MapWallDisplayActions.hideMapWallDisplay()
        ];
      })
    )
  );

  deleteSelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.deleteSelectedMap),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapForDelete),
        this.store$.select(copSelectors.selectMaps),
        this.store$.select(copSelectors.selectSelectedMapState)
      ),
      switchMap(([action, selectedMapForDelete, maps, mapState]) => {
        const params: QueryParams = {
          skip: 0,
          take: 1000,
          where: null,
          sortField: 'name',
          sortDirection: 'Ascending'
        };

        const mapToDelete = maps.find(m => m.uId === selectedMapForDelete);
        const isDeletingCurrentMap = mapState.map.uId === selectedMapForDelete;

        return this.mapApiService.deleteMap(mapToDelete).pipe(
          switchMap(res => this.mapApiService.getMaps(params)),
          switchMap(res => {
            if (isDeletingCurrentMap) {
              this.spinnerService.show('map');

              return [
                copActions.clearMap({ options: null }),
                copActions.deleteSelectedMapCompleteToStore(),
                copActions.clearMapForDeleteToStore(),
                copActions.loadMapsForUserCompleteToStore({ maps: res.data }),
                copActions.setSelectedMap({ uId: res?.data[0]?.uId })
              ];
            } else {
              return [copActions.clearMapForDeleteToStore(), copActions.loadMapsForUserCompleteToStore({ maps: res.data })];
            }
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  getMapLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.queryForMapLinks),
      switchMap(action => {
        return this.mapApiService.getMapLinks(action.params).pipe(
          map(res => copActions.queryForMapLinksCompleteToStore({ results: res })),
          catchError(err =>
            of(
              copActions.setError({ err }),
              copActions.queryForMapLinks({
                params: {
                  skip: 0,
                  take: 1000,
                  totalCount: 0
                }
              })
            )
          )
        );
      })
    )
  );

  deleteMapLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.deleteMapLink),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, selectedMapState]) => {
        return this.mapApiService.deleteMapLink(action.uId).pipe(
          mergeMap(result => [
            copActions.deleteMapLinkCompleteToStore({ uId: action.uId }),
            copActions.refreshMapRoom({ mapUId: selectedMapState.map.uId })
          ]),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  shareMapLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.shareMapLink),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, selectedMapState]) => {
        return this.mapApiService.createMapLink(action.uId).pipe(
          mergeMap(result => [
            copActions.shareMapLinkCompleteToStore({ mapLink: result }),
            copActions.refreshMapRoom({ mapUId: selectedMapState.map.uId })
          ]),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  refreshMapRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.refreshMapRoom),
      switchMap(action => {
        const params: QueryParams = {
          skip: 0,
          take: 1000,
          where: null,
          sortField: 'name',
          sortDirection: 'Ascending'
        };

        return this.mapApiService.getMaps(params).pipe(
          map(res => copActions.refreshMapRoomCompleteToStore({ results: res, selectedMapUId: action.mapUId })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  getMapRoomCollaborators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.getMapRoomCollaborators),
      switchMap(action => {
        return this.contentShareApiService.getCollaborators(action.contentUIds).pipe(
          map(result => {
            return copActions.getMapRoomCollaboratorsCompleteToStore({ results: result });
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  getMapLayerViewFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.getMapLayerViewFromApi),
      switchMap(action => {
        return this.mapApiService.getMapLayerView().pipe(
          switchMap(results => {
            return [copActions.getMapLayerViewFromApiCompleteToStore({ results })];
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  newMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.newMap),
      withLatestFrom(this.store$.select(copSelectors.selectMaps)),
      switchMap(([action, maps]) => {
        return this.translate.get(['MAP.NEW-MAP-NAME', 'MAP.ROOT-FOLDER-NAME']).pipe(
          switchMap(res => {
            const mapName = res['MAP.NEW-MAP-NAME'];
            const folderName = res['MAP.ROOT-FOLDER-NAME'];

            const rootFolderUid = uuidv4();

            const sections: Folder[] = [];
            let i = 0;
            action.values.sections.forEach(sectionInit => {
              let layers: Layer[] = [];
              // Configure Layers here

              sections.push({
                uId: uuidv4(),
                parentUId: rootFolderUid,
                name: sectionInit.name,
                folders: [],
                layers,
                sortOrder: i,
                sectionType: sectionInit.type
              } as Folder);
            });

            const newMap = {
              uId: GUID_EMPTY,
              name: action.values.name,
              isActive: true,
              version: 1,
              displaySettings: action.values.displaySettings,
              folders: [
                {
                  uId: rootFolderUid,
                  name: folderName,
                  folders: sections,
                  layers: [],
                  sortOrder: 0
                } as Folder
              ],
              createdOn: null,
              lastEditedById: null,
              lastEditedByName: null,
              updatedOn: null
            } as Map;

            const settings: MapSettings = {} as MapSettings;

            this.spinnerService.show('map');

            const mapWithState = this.mapStateService.initMapStateFromMap(newMap, settings);

            return [
              copActions.clearMap({ options: null }),
              copActions.newMapCompleteToStore({ newMapState: mapWithState }),
              copActions.setSelectedMap({ uId: newMap.uId })
            ];
          })
        );
      })
    )
  );

  copySelectedMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.copySelectedMap),
      withLatestFrom(this.store$.select(copSelectors.selectMaps), this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, maps, mapState]) => {
        return this.translate.get(['MAP.NEW-MAP-NAME']).pipe(
          switchMap(res => {
            let mapName = action.mapName || res['MAP.NEW-MAP-NAME'];

            let oldMap = maps.find(m => m.uId === mapState.map.uId);
            if (!action.mapName && oldMap) {
              let i = 0;
              do {
                mapName = i ? `${oldMap.name} copy ${i}` : `${oldMap.name} copy`;
                i++;
              } while (maps.find(m => m.name === mapName));
            }

            let newMap: Map = {
              uId: GUID_EMPTY,
              name: action.mapName || mapName,
              isActive: true,
              version: oldMap.version || 0,
              folders: [],
              createdOn: null,
              lastEditedById: null,
              lastEditedByName: null,
              updatedOn: null
            } as Map;

            // Convert all GUIDs to new guids for save of new map
            newMap.folders = [
              ...FolderUtils.mapFolders(
                oldMap.folders,
                (folder, parentFolder) => {
                  // Parent will be the new folder with translated UId
                  folder.parentUId = parentFolder?.uId;
                  folder.uId = uuidv4();
                  return folder;
                },
                (layer, parentFolder) => {
                  // Parent will be the new folder with translated UId
                  layer.folderUId = parentFolder?.uId;
                  layer.uId = uuidv4();
                  return layer;
                }
              )
            ];

            const mapWithState = this.mapStateService.initMapStateFromMap(newMap, {} as MapSettings, true);

            const updatedMaps = maps.slice();
            updatedMaps.unshift(newMap);

            return [copActions.newMapCompleteToStore({ newMapState: mapWithState }), copActions.setSelectedMap({ uId: newMap.uId })];
          })
        );
      })
    )
  );

  getMapAndCopy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.getMapAndCopy),
      switchMap(action => {
        return this.mapApiService.getMap(action.uId).pipe(
          switchMap(map => {
            return [copActions.copyMap({ map })];
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  copyMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.copyMap),
      withLatestFrom(this.store$.select(copSelectors.selectMaps)),
      switchMap(([action, maps]) => {
        return this.translate.get(['MAP.NEW-MAP-NAME']).pipe(
          switchMap(res => {
            let mapName = action.map.name || res['MAP.NEW-MAP-NAME'];

            let oldMap = action.map;
            let i = 0;
            do {
              mapName = i ? `${oldMap.name} copy ${i}` : `${oldMap.name} copy`;
              i++;
            } while (maps.find(m => m.name === mapName));

            let newMap: Map = {
              uId: GUID_EMPTY,
              name: mapName,
              isActive: true,
              version: oldMap.version || 0,
              folders: [],
              createdOn: null,
              lastEditedById: null,
              lastEditedByName: null,
              updatedOn: null
            } as Map;

            // Convert all GUIDs to new guids for save of new map
            newMap.folders = [
              ...FolderUtils.mapFolders(
                oldMap.folders,
                (folder, parentFolder) => {
                  // Parent will be the new folder with translated UId
                  folder.parentUId = parentFolder?.uId;
                  folder.uId = uuidv4();
                  return folder;
                },
                (layer, parentFolder) => {
                  // Parent will be the new folder with translated UId
                  layer.folderUId = parentFolder?.uId;
                  layer.uId = uuidv4();
                  return layer;
                }
              )
            ];

            const mapWithState = this.mapStateService.initMapStateFromMap(newMap, {} as MapSettings, true);

            const updatedMaps = maps.slice();
            updatedMaps.unshift(newMap);

            return [copActions.newMapCompleteToStore({ newMapState: mapWithState }), copActions.setSelectedMap({ uId: newMap.uId })];
          })
        );
      })
    )
  );

  // layers

  calculateLayerFilterOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.calculateLayerFilterOptions),
      withLatestFrom(
        this.store$.select(copSelectors.selectDatasetsSet),
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectLayerVisibility),
        this.store$.select(copSelectors.selectMapPosition)
      ),
      switchMap(([action, datasetsSet, mapState, layerVisibility, mapPosition]) => {
        const options: CalculatedLayerFilterOptions[] = [];
        Object.keys(mapState.layerStates).forEach(layerId => {
          const layer = mapState.layers[layerId];
          const currentMapZoom = mapPosition.zoomLevel;
          if (currentMapZoom > layer.maxz || currentMapZoom < layer.minz) {
            return [];
          }
          if (!layer?.displayOptions?.floorFilterSourceField) {
            return [];
          }

          const dataset = datasetsSet[layer.datasetUId];
          if (!dataset) {
            return [];
          }
          const floorFilterFieldSchema = dataset.schema.fields.find(f => f.systemName === layer.displayOptions?.floorFilterSourceField);
          if (floorFilterFieldSchema && !options.find(o => o.datasetUId === dataset.uId)) {
            options.push({
              datasetUId: dataset.uId,
              displayName: dataset.name,
              fieldName: floorFilterFieldSchema.displayName,
              picklistValues: floorFilterFieldSchema.picklistValues.filter(p => p.isActive)
            });
          }
        });
        return [copActions.setCalculateLayerFilterOptionsToStore({ layerFilterOptions: options }), ...(action.then || [])];
      })
    )
  );

  mapMapPositionChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.mapMapPositionChanged),
      withLatestFrom(this.store$.select(copSelectors.selectMapPosition)),
      switchMap(([action, mapPosition]) => {
        if (isEqual(action.mapPosition, mapPosition)) {
          return [];
        }
        return [copActions.setMapPositionSettingsToStore({ mapPosition: action.mapPosition })];
      })
    )
  );

  // Layer/Page

  updateVisibleWorkflowStepsOfDocumentsForAlertMapLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateVisibleWorkflowStepsOfDocumentsForMapLayers),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectWorkflowStatusVisibilityByLayerTypeAndName)
      ),
      switchMap(([action, mapState, workflowVisibility]) => {
        const { layerUId, sectionUId, hide, stepName, noRedraw, clearAll } = action;

        if (!Boolean(sectionUId) || !Boolean(stepName)) {
          return [];
        }

        const selectedAlertsFolder = mapState.foldersSet[sectionUId];
        const layers = selectedAlertsFolder?.layers ?? [];

        // * NOTE * - converting to a base map following previous existing structure.
        // * TBR * - this is stored as stringified JSON currently, can't completely refactor at time of fixing Layer visibility issue #4695
        const workflowStatusVisibility = layers.reduce(
          (acc, layer) => {
            const layerVisibilityKey = `${sectionUId}-${layer.uId}`;
            return {
              ...acc,
              [layerVisibilityKey]: workflowVisibility[layerVisibilityKey] ?? []
            };
          },
          { [sectionUId]: workflowVisibility[sectionUId] ?? [] } as Record<string, string[]>
        );

        // -- handle selected workflow status layer --
        const hasSelectedLayerUId = Boolean(layerUId);
        const visibilityKey = hasSelectedLayerUId ? `${sectionUId}-${layerUId}` : sectionUId;

        // * NOTE * - if no layer uid present, then it is section/folder, which means cascade to nested layers
        for (const [key, values = []] of Object.entries(workflowStatusVisibility)) {
          if (key.startsWith(visibilityKey)) {
            workflowStatusVisibility[key] = hide
              ? Array.from(new Set([...values, stepName]))
              : clearAll
                ? []
                : values.filter(v => v !== stepName);
          }
        }

        // * NOTE * - check if all of the updated layer's sibling layers are of the same status and update section accordingly
        if (hasSelectedLayerUId) {
          const allSectionLayersHidden = Object.entries(workflowStatusVisibility)
            .reduce((acc, [key, values]) => {
              if (key.startsWith(`${sectionUId}-`)) {
                acc.push(values.includes(stepName));
              }
              return acc;
            }, [])
            .every(status => status);

          const sectionStatuses = workflowStatusVisibility[sectionUId] ?? [];

          workflowStatusVisibility[sectionUId] = allSectionLayersHidden
            ? Array.from(new Set([...sectionStatuses, stepName]))
            : sectionStatuses.filter(v => v !== stepName);
        }

        const actionsForDispatch: Action[] = [
          copActions.setWorkflowStatusVisibilityByLayerTypeAndNameToStore({ workflowStatusVisibility })
        ];

        if (!noRedraw) {
          actionsForDispatch.push(copActions.calculateWorkflowStatusSummary({ summaryItems: null }));
        }

        return actionsForDispatch;
      })
    )
  );

  updateWorkflowStepsExpandedForAlertMapLayers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateWorkflowStepsExpandedForMapLayers),
      withLatestFrom(this.store$.select(copSelectors.selectWorkflowStatusExpandedByLayerTypeAndName)),
      switchMap(([action, workflowExpanded]) => {
        const workflowStepExpandedKey = action.sectionUId ? action.sectionUId : LayerTypes[action.layerType];
        const workflowExpandedForThisLayerType = workflowExpanded[workflowStepExpandedKey] || [];

        const newWorkflowExpanded = { ...workflowExpanded };
        if (!action.expanded && workflowExpandedForThisLayerType.find(v => v === action.stepName)) {
          // Remove hide for this step
          newWorkflowExpanded[workflowStepExpandedKey] = workflowExpandedForThisLayerType.filter(v => v !== action.stepName);
        }
        if (action.expanded && !workflowExpandedForThisLayerType.find(v => v === action.stepName)) {
          // Add this step
          newWorkflowExpanded[workflowStepExpandedKey] = [...workflowExpandedForThisLayerType, action.stepName];
        }

        return [copActions.setWorkflowStatusExpandedByLayerTypeAndNameToStore({ workflowStatusExpanded: newWorkflowExpanded })];
      })
    )
  );

  setWorkflowStatusSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.setWorkflowStatusSummary),
      withLatestFrom(this.store$.select(listsSelectors.selectListQuery)),
      switchMap(([action, listQuery]) => {
        const actions: any[] = [copActions.setWorkflowStatusSummaryCompleteToStore({ summary: action.summary })];

        // check to see if the table is focused on a workflow summary
        // if so then reload the table
        if (!!listQuery?.data?.summaryName) {
          let summary = action?.summary?.length > 0 ? action.summary[0] : null;
          if (summary && listQuery?.data?.sectionUId) {
            summary = action.summary?.find(s => s.sectionUId === listQuery?.data?.sectionUId);
          }
          if (!!summary) {
            const layerUIds = Object.keys(listQuery?.data.layerQueries);
            const summaryForStatus = summary.summaryByStatus.find(s => s.name === listQuery.data?.summaryName) as MapLayerSummaryByStatus;
            if (!!summaryForStatus) {
              const newSummary = produce(summaryForStatus, draftState => {
                draftState.layerDocs = draftState.layerDocs.filter(d => layerUIds.includes(d.layerUId));
              });

              actions.push(
                listsActions.loadSummaryDocumentsForLists({
                  sectionUId: listQuery?.data?.sectionUId,
                  summaryForStatus: newSummary,
                  summaryByStatus: summary.summaryByStatus,
                  userClick: false
                })
              );
            }
          }
        }

        return actions;
      })
    )
  );

  updateSectionWidgetUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateSectionWidgetUserSettings),
      withLatestFrom(
        this.store$.select(copSelectors.selectSectionWidgetUserSettings),
        this.store$.select(copSelectors.selectSelectedMapState)
      ),
      switchMap(([action, sectionWidgetUserSettings, mapState]) => {
        const newValue = { ...sectionWidgetUserSettings };
        let viewModeChanged = false;
        if (action.view) {
          if (newValue[action.sectionUId]?.viewMode !== action.view) {
            viewModeChanged = true;
          }
          newValue[action.sectionUId] = {
            ...newValue[action.sectionUId],
            viewMode: action.view
          };
        } else if (newValue[action.sectionUId]?.viewMode) {
          if (newValue[action.sectionUId]?.viewMode) {
            viewModeChanged = true;
          }
          delete newValue[action.sectionUId].viewMode;
        }

        if (action.summary) {
          newValue[action.sectionUId] = {
            ...newValue[action.sectionUId],
            summary: action.summary
          };
        } else if (newValue[action.sectionUId]?.summary) {
          delete newValue[action.sectionUId].summary;
        }

        const layerActions = [];
        layerActions.push(copActions.setSectionWidgetUserSettingsToStore({ sectionWidgetUserSettings: newValue }));

        // If view mode changed, update layers to draw correct new colors
        if (viewModeChanged && mapState.map?.folders?.length > 0) {
          const section = mapState.foldersSet[action.sectionUId];
          if (!!section && section.sectionType > 0) {
            const layersInSection = [
              ...section.layers.filter(l => l.type === LayerTypes.Alert),
              ...Object.values(mapState.layersSet).filter(
                l => section.folders.find(f => f.uId === l.folderUId) && l.type === LayerTypes.Alert
              )
            ];
            const folderUIds = {};

            // Clear out impact zone geos from the section
            layerActions.push(copActions.updateSecondaryGeoVisibility({ sectionUId: action.sectionUId, action: 'remove-all' }));
            layerActions.push(
              copActions.updateVisibleWorkflowStepsOfDocumentsForMapLayers({
                sectionUId: section.uId,
                hide: false,
                noRedraw: true,
                clearAll: true
              })
            );

            layersInSection?.forEach(layer => {
              folderUIds[layer.folderUId] = true;
              layerActions.push(copActions.refreshLayerDocumentsOnMap({ layerUId: layer.uId }));
            });

            layerActions.push(copActions.showLayers({ layers: layersInSection }));
          }
        }

        return layerActions;
      })
    )
  );

  refreshLayerDocumentsOnMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.refreshLayerDocumentsOnMap),
      switchMap(action => {
        setTimeout(() => {
          this.mapDrawingService.refreshLayerDocuments(action.layerUId, action.documentUIds);
        }, 0);
        return [];
      })
    )
  );

  checkDataChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.checkDataChanged),
      withLatestFrom(
        this.store$.select(listsSelectors.selectListQuery),
        this.store$.select(rsdSelectors.selectSelectedDocumentState),
        this.store$.select(rsdSelectors.selectIsDrawerOpen),
        this.store$.select(rsdSelectors.selectIsEdit)
      ),
      switchMap(([action, query, documentState, isOpen, isEdit]) => {
        const actions = [];

        // check if selected document in RSD has changed
        const selectedDocumentChanged = action.documentUIds?.find(uid => uid === documentState?.document?.uid);

        if (isEdit === false && !!selectedDocumentChanged) {
          if (action.action !== 'delete') {
            // refresh the document
            actions.push(
              rsdActions.setSelectedDocument({
                layerUId: action.layerUId,
                documentUId: documentState.document.uid,
                isDrawerOpen: isOpen,
                replaceStack: true
              })
            );
          } else {
            // document was deleted
            actions.push(rsdActions.cancelEditDocument());
            actions.push(rsdActions.hideDrawer({ clearDocumentStack: true }));
          }
        }

        // check if list query needs update
        if (!!query && query.data?.layerQueries[action.layerUId]) {
          if (query.data?.type !== 'cluster') {
            actions.push(listsActions.setLayerQueryDataUpdateTime({ layerUId: action.layerUId }));
          } else {
            // the 'add' action will be evaluated when new cluster will be created with update count
            // cluster will call checkClusterDataUpdateChanged for update
            if (action.action === 'delete' || action.action === 'update') {
              if (action.documentUIds?.some(uid => query.data?.layerQueries[action.layerUId].documents.includes(uid))) {
                actions.push(listsActions.setLayerQueryDataUpdateTime({ layerUId: action.layerUId }));
              }
            }
          }
        }
        return actions;
      })
    )
  );

  checkClusterDataChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.checkClusterDataChanged),
      withLatestFrom(this.store$.select(listsSelectors.selectListQuery)),
      switchMap(([action, query]) => {
        const actions = [];

        if (query?.data?.type === 'cluster') {
          Object.keys(action.layerDocuments).forEach(l => {
            if (query?.data?.layerQueries[l]?.clusterPosition === action.position) {
              actions.push(listsActions.setLayerQueryDataUpdateTime({ layerUId: l }));
            }
          });
        }

        return actions;
      })
    )
  );

  updateSecondaryGeoVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateSecondaryGeoVisibility),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectGeoFieldsForOptionalDisplay)
      ),
      switchMap(([action, mapState, geoFieldsForOptionalDisplay]) => {
        var newGeo = geoFieldsForOptionalDisplay?.slice() || [];
        var layersInSection = [];
        var updateLayerActions = [];

        if (mapState.map?.folders?.length > 0 && action.sectionUId) {
          const section = mapState.foldersSet[action.sectionUId];

          if (!!section && section.sectionType > 0) {
            layersInSection = [...section.layers, ...Object.values(mapState.layersSet)];
          }
        } else if (mapState.map?.folders?.length > 0 && action.layerUId && !action.sectionUId) {
          layersInSection = [...Object.values(mapState.layersSet).filter(layer => layer.uId === action.layerUId)];
        }

        layersInSection.forEach(layer => {
          const extraGeoFieldName =
            layer.displayOptions?.optionalGeoFieldsToDisplay?.length > 0 ? layer.displayOptions?.optionalGeoFieldsToDisplay[0] : null;
          const fieldExists = newGeo.find(g => g.layerUId === layer.uId && g.stepUId === action?.stepUId);

          if (fieldExists && action.action === 'remove') {
            newGeo = newGeo?.filter(n => n.layerUId !== fieldExists.layerUId && n.stepUId !== fieldExists.stepUId);
          } else if (action.action === 'remove-all') {
            newGeo = newGeo?.filter(n => n.layerUId !== layer.uId);
          } else if (action.action === 'add') {
            const newFieldNames = layer.displayOptions?.optionalGeoFieldsToDisplay;
            newGeo.push({ layerUId: layer.uId, stepUId: action.stepUId, fieldNames: newFieldNames });
          }
          let updateDocuments = null;
          if (!!extraGeoFieldName && !!mapState?.layerStates[layer.uId]?.documents) {
            updateDocuments = Object.values(mapState.layerStates[layer.uId].documents).filter(d => !!d[extraGeoFieldName]);
          }
          updateLayerActions.push(copActions.updateMapDrawingDocumentsForLayer({ layerUId: layer.uId, documents: updateDocuments }));
        });

        return [copActions.setGeoFieldsForOptionalDisplayToStore({ geoFieldsForOptionalDisplay: newGeo }), ...updateLayerActions];
      })
    )
  );

  updateMapDrawingDocumentsForLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.updateMapDrawingDocumentsForLayer),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { layerUId, documents } = action;

        const updateDocuments = !!documents
          ? documents
          : mapState.layerStates[layerUId]
            ? Object.values(mapState.layerStates[layerUId])
            : [];

        const event: LayerDataChangeEvent = {
          layerUId: layerUId,
          changes: {},
          type: LayerDataChangeType.Update,
          totalDocumentCount: updateDocuments.length
        };
        updateDocuments.forEach(d => (event.changes[d.uid] = d));

        setTimeout(() => {
          this.mapDrawingService.processUpdateEvent(event);
        }, 10);
        return [];
      })
    )
  );

  refreshLayerDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.refreshLayerDocuments),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { layerUId, dataset, documents } = action;

        const actions = [];
        let layerUIds = [];

        if (!!layerUId) {
          layerUIds.push(layerUId);
        } else {
          layerUIds = Object.values(mapState.layersSet)
            .filter(l => l.datasetUId === dataset.uId)
            ?.map(l => l.uId);
        }

        layerUIds.forEach(l => {
          let changeEvent = {
            type: LayerDataChangeType.Update,
            layerUId: l,
            changes: {}
          } as LayerDataChangeEvent;

          documents.forEach(d => {
            changeEvent.changes[d.uid] = d;
          });

          actions.push(copActions.processLayerDataChangeEvents({ events: [changeEvent] }));
        });
        return actions;
      })
    )
  );

  removeLayerDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.removeLayerDocuments),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { layerUId, dataset, documents } = action;

        const actions = [];
        let layerUIds = [];

        if (!!layerUId) {
          layerUIds.push(layerUId);
        } else {
          layerUIds = Object.values(mapState.layersSet)
            .filter(l => l.datasetUId === dataset.uId)
            ?.map(l => l.uId);
        }

        layerUIds.forEach(l => {
          let changeEvent = {
            type: LayerDataChangeType.Remove,
            layerUId: l,
            changes: {}
          } as LayerDataChangeEvent;

          documents.forEach(d => {
            changeEvent.changes[d.uid] = d;
          });

          actions.push(copActions.processLayerDataChangeEvents({ events: [changeEvent] }));
        });
        return actions;
      })
    )
  );

  removeSelectedMapDrawingAndDocumentsForLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.removeSelectedMapDrawingAndDocumentsForLayer),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { layerUId, then } = action;

        this.mapDrawingService.removeLayerDocuments(layerUId);
        mapState = this.mapStateService.removeLayerDocuments(mapState, layerUId);

        return [...(then || [])];
      })
    )
  );

  removeSelectedMapDrawingAndDocumentsForFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.removeSelectedMapDrawingAndDocumentsForFolder),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const { folderUId, then } = action;

        const folder = mapState.foldersSet[folderUId];
        folder.layers?.forEach(l => {
          this.mapDrawingService.removeLayerDocuments(l.uId);
          mapState = this.mapStateService.removeLayerDocuments(mapState, l.uId);
        });

        return [...(then || [])];
      })
    )
  );

  loadDocumentsForMapLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.loadDocumentsForMapLayer),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedFloor),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections)
      ),
      switchMap(([action, mapState, floor, quickFilters]) => {
        const { layerUId } = action;

        const layer = mapState.layersSet[layerUId];
        const dataset = mapState.datasetsSet[layer.datasetUId];

        if (!!dataset) {
          this.mapDataWorkerService.populateAndTrackLayer({
            layer: layer,
            dataset: mapState.datasetsSet[layer.datasetUId],
            layerQuickFilter: quickFilters[layerUId],
            floor
          });

          return [];
        } else {
          return this.datasetApiService.getDataset(layer.datasetUId, { includeWorkflow: true }).pipe(
            switchMap(res => {
              const updatedMapState = produce(mapState, draftState => {
                draftState.datasetsSet[layer.datasetUId] = res;
              });

              this.mapDataWorkerService.populateAndTrackLayer({
                layer: layer,
                dataset: res,
                layerQuickFilter: quickFilters[layerUId],
                floor
              });
              return [copActions.loadMapWithStateCompleteToStore({ mapWithState: updatedMapState })];
            }),
            catchError(_ => {
              return of([]);
            })
          );
        }
      })
    )
  );

  transitionDocumentWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.transitionDocumentWorkflows),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      mergeMap(([action, mapState]) => {
        var updatedDocs: Document[] = [];

        action.documents.forEach(doc => {
          const writer = new DocumentWriter(doc);
          writer.workflowStepUId = action.workflowStepUId;
          updatedDocs.push(writer.document);
        });

        const updateRequest = {
          documents: updatedDocs
        };

        const datasetUId = action.datasetUId;

        return this.dataApiService.updateDocuments(datasetUId, updateRequest).pipe(
          switchMap(results => []),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  deleteLayerDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.deleteLayerDocuments),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(rsdSelectors.selectSelectedDocumentState)),
      switchMap(([action, mapState, documentState]) => {
        var selectedDocDeleted = false;
        var docsToDelete: DocumentsToDelete[] = [];

        const layer = mapState.layersSet[action.layerUId];
        const dataset = mapState.datasetsSet[layer.datasetUId];

        const deleteObj: DocumentsToDelete = {
          datasetUId: layer.datasetUId,
          documents: action.documents
        };

        docsToDelete.push(deleteObj);

        return this.dataApiService.deleteDocuments(docsToDelete).pipe(
          switchMap(results => {
            if (MapLayerQueryBuildService.isUsingPollingQuery(layer, dataset)) {
              return [copActions.removeLayerDocuments({ dataset, documents: action.documents, layerUId: layer.uId })];
            }

            return [];
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  zoomToDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.zoomToDocument),
      switchMap(action => {
        const { layer, dataset, document } = action;

        let features = this.mapDrawingService.getDocumentMainGeoValueFeatures(layer, dataset, document);

        if (!!!features) {
          return [];
        }

        const screenSize = screen.height - 200;
        const zoomLevels = this.utilService.findMinGoogleZoomToCoverAtLeastNPixels(features, screenSize);

        const geoJsonBounds = this.distanceService.getBoundsForGeometryFeatures(features);
        if (!!!geoJsonBounds) {
          return [];
        }

        const foundLocation = this.utilService.getZoomLocationFromBounds(geoJsonBounds, layer);
        zoomLevels.push(foundLocation.zoomLevel);

        const defaultZoomLevel = layer.displayOptions.zoomToFeatureLevel ?? 17;
        let zoomLevel = zoomLevels.reduce((prev, current) => {
          const val = current <= prev && current > 0 ? current : prev;
          return val;
        }, defaultZoomLevel);

        // Zooming to a feature that has a single polygon as a geometry will center and
        //   zoom to a level that allows the user to see the entire polygon but as zoomed in
        //   as possible.  The "User Zoom to Feature Level" setting is ignored. (NOT LineString or Point)

        // Zooming to a feature that has multiple pins or polygons or a cluster will center and zoom to the maximum level that will display all the
        //   pins/polygons in the cluster.
        if (
          features.length > 1 ||
          (features.length === 1 &&
            ((features[0]?.type?.toLowerCase()?.includes('feature') &&
              features[0].geometry?.type?.toLowerCase() !== 'point' &&
              features[0].geometry?.type?.toLowerCase() !== 'linestring') ||
              (!features[0].type?.toLowerCase()?.includes('feature') &&
                features[0].type?.toLowerCase() !== 'point' &&
                features[0].type?.toLowerCase() !== 'linestring')))
        ) {
          zoomLevel = this.distanceService.findMinGoogleZoomToCoverAtMostNPixelsForFeature(features, screenSize);
        }

        const boundsGeo = foundLocation.geometry as BboxGeometry;

        this.mapDrawingService.setCenterAndZoom(boundsGeo.center.lat, boundsGeo.center.lng, zoomLevel);
        return [listsActions.hideTableToStore()];
      })
    )
  );

  setFloorFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.setFloorFilter),
      map(action => {
        const { floor } = action;

        this.mapDataWorkerService.setFloorFilterForLayers(floor);

        return copActions.setFloorFilterComplete({ floor });
      })
    )
  );

  getTileLayerDocumentsFromMapClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.getTileLayerDocumentsFromMapClick),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectMapPosition),
        this.store$.select(copSelectors.selectMapSettings),
        this.store$.select(copSelectors.selectSelectedFloor)
      ),
      switchMap(([action, mapState, mapPosition, settings, selectedFloor]) => {
        if (!mapState?.layers || !mapPosition?.zoomLevel) {
          return [];
        }

        // calculate the distance for a single pixel to be used in the expanding radius method
        const pixelSizeMeters = this.distanceService.getDistanceFromPixels(mapPosition.zoomLevel, action.lat, 2);
        const pixelSizeInMeters = Math.round(pixelSizeMeters);

        const topoTileLayers = action.layers.map(tl => {
          return {
            layerUId: tl.uId,
            datasetUId: tl.datasetUId,
            filterGroup: tl.rule.root,
            floorFilterSourceField: tl.displayOptions?.floorFilterSourceField
          };
        });

        if (!(topoTileLayers?.length > 0)) {
          return [];
        }

        const request: LayerDocumentsInClickRadiusRequest = {
          layers: topoTileLayers,
          latitude: action.lat,
          longitude: action.lng,
          pixelSizeInMeters,
          selectedFloor
        };

        return this.dataApiService.getLayerDocumentsInClickRadius(request).pipe(
          switchMap(results => {
            const layerUIds = Object.keys(results);
            // check if we clicked on a single document and need to select it
            if (layerUIds.length === 1 && results[layerUIds[0]]?.length === 1) {
              const layerUId = layerUIds[0];
              const document = results[layerUId][0];
              return [
                listsActions.clearCardsToStore(),
                rsdActions.setSelectedDocument({ layerUId, documentUId: document.uid, isDrawerOpen: true, replaceStack: true, edit: false })
              ];
            }

            // check if we have multiple matches and need to show the card view
            if (layerUIds.some(layerUId => results[layerUId]?.length > 0)) {
              const layerDocuments: LayerDocumentUIdsSet = {};
              layerUIds.map(layerUId => {
                layerDocuments[layerUId] = results[layerUId].map(d => d.uid);
              });
              return [
                listsActions.loadClusterDocumentsForLists({ layerDocuments, position: null, zoomToBounds: null }),
                rsdActions.showDrawer(),
                listsActions.showCardsToStore()
              ];
            }

            return [copActions.getTileLayerDocumentsFromMapClickComplete({ results })];
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  ensureDocumentDatasetsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.ensureDocumentDatasetsLoaded),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const actionsToReturn = [];
        action.datasetUIds.forEach(datasetUId => {
          if (!datasetUId) {
            return;
          }
          const dataset = mapState.datasetsSet[datasetUId];
          if (!dataset) {
            actionsToReturn.push(
              this.datasetApiService.getDataset(datasetUId, { includeWorkflow: true }).pipe(
                catchError(err => {
                  const errorMessage = this.translate.instant(`MAP.ERROR-LINK-DATASET`);
                  this.notify.error(errorMessage);
                  console.error(errorMessage + ': ' + datasetUId);
                  return of(null);
                })
              )
            );
          }
        });

        return forkJoin(actionsToReturn).pipe(
          mergeMap(data => {
            let datasetsToAdd = [];
            data?.forEach(dataset => {
              if (!!dataset) {
                datasetsToAdd.push(copActions.addDatasetToStore({ dataset: dataset }));
              }
            });
            return datasetsToAdd;
          })
        );
      })
    )
  );

  addDocumentDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.addDocumentDataset),
      switchMap(action => {
        return this.datasetApiService.getDataset(action.datasetUId, { includeWorkflow: true }).pipe(
          map(dataset => {
            return copActions.addDatasetToStore({ dataset });
          }),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  createSiteWatchLayerAndSelectDocumentAndDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.createSiteWatchLayerAndSelectDocumentAndDataset),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentStackCount)),
      switchMap(([action, count]) => {
        const ghostLayerUId = 'GHOST-LAYER';
        const actions = [];
        if (count > 1) {
          actions.push(rsdActions.popDocumentStateStack());
        }
        actions.push(
          copActions.addGhostLayerToStore({ layerUId: ghostLayerUId, datasetUId: action.datasetUId, document: action.document })
        );
        actions.push(
          rsdActions.setSelectedDocument({
            layerUId: ghostLayerUId,
            documentUId: action.document.uid,
            isDrawerOpen: true,
            replaceStack: !action.setBackAction,
            backAction: !!action.setBackAction ? siteWatchActions.showCards.type : null
          })
        );
        actions.push(listsActions.hideCardsToStore());
        return actions;
      })
    )
  );

  checkDocumentsMatchLayerFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.checkDocumentsMatchLayerFilter),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections)
      ),
      switchMap(([, mapState, quickFilters]) => {
        const events: LayerDataChangeEvent[] = [];

        if (!mapState && !mapState?.layers) {
          return [];
        }

        // only process layers that are active, have a dataset and a filter
        const layersToProcess = mapState?.layers?.filter(
          l => l.isActive && !!l.datasetUId && (l.rule?.root?.filterGroups?.length > 0 || l.rule?.root?.filters?.length > 0)
        );

        if (!layersToProcess || layersToProcess?.length === 0) {
          return [];
        }

        for (let layer of layersToProcess) {
          const changes: DocumentsSet = {};
          const dataset = mapState.datasetsSet[layer.datasetUId];
          let filterGroup = layer.rule?.root;
          filterGroup = QuickFilterHelperService.addQuickFiltersIfActive(filterGroup, layer, dataset, quickFilters[layer.uId]);

          const schema = dataset?.schema;
          if (!schema) {
            continue;
          }

          const layerState = mapState.layerStates[layer.uId];
          if (!layerState?.documents) {
            continue;
          }

          const documents = Object.values(layerState.documents);

          for (let document of documents) {
            if (!this.filterRuntimeService.documentMatchesFilter(document, filterGroup, schema)) {
              changes[document.uid] = document;
            }
          }

          if (Object.keys(changes).length > 0) {
            events.push({
              type: LayerDataChangeType.Remove,
              layerUId: layer.uId,
              changes
            });
          }
        }

        return events.length > 0 ? [copActions.processLayerDataChangeEvents({ events })] : [];
      })
    )
  );

  zoomToDocumentByUIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.zoomToDocumentByUIds),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const dataset = mapState.datasetsSet[action.datasetUId];
        if (!dataset) {
          return [];
        }

        let layer: Layer = null;
        let document: Document = null;
        const possibleLayers = mapState.layers.filter(l => l.datasetUId === dataset.uId);
        for (let possibleLayer of possibleLayers) {
          if (!!mapState.layerStates[possibleLayer.uId]?.documents[action.documentUId]) {
            layer = possibleLayer;
            document = mapState.layerStates[possibleLayer.uId].documents[action.documentUId];
            break;
          }
        }

        if (!layer || !document) {
          return [];
        }

        return [copActions.zoomToDocument({ layer, dataset, document })];
      })
    )
  );

  hideWallDisplayOnNewMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(copActions.showNewMapToStore),
      map(() => MapWallDisplayActions.hideMapWallDisplay())
    )
  );
}

const createSelectedDocumentLayer = (dataset: Dataset, layerName: string, filterDocumentUId?: string) => {
  const geometryFields = dataset.schema.fields.filter(f => f?.dataType === FieldDataType.Geometry);
  const locationField = geometryFields.find(f => f.systemName === SystemFieldNames.location);
  const defaultGeometryFieldToDraw = locationField ?? geometryFields[0];

  let filters: Filter[] = [];

  if (filterDocumentUId) {
    filters.push({
      field: 'uid',
      operator: RuleOperator.Equal,
      value: filterDocumentUId,
      options: null,
      filterOptions: null
    });
  }

  let layer = {
    uId: TemporaryLayerUIds.SelectedDocumentLayerUId,
    folderUId: TemporaryFolderUIds.SelectedDocumentFolderUId,
    name: layerName,
    isActive: true,
    color: dataset.defaultColor,
    type: LayerTypes.Other,
    isCustomIcon: dataset.isCustomIcon,
    iconPath: dataset.iconPath,
    datasetUId: dataset.uId,
    displayOptions: {
      geoFieldsToDisplay: [defaultGeometryFieldToDraw.systemName]
    } as DisplayOptions,
    rule: {
      uId: GUID_EMPTY,
      datasetUId: null,
      root: {
        uId: GUID_EMPTY,
        filters: filters,
        filterGroups: [],
        groupType: FilterGroupType.And
      }
    }
  } as Layer;

  return layer;
};
